import React, { useEffect, useState } from 'react'
import { Resource, ResourceFile, ResourceFolder, User } from '../../utils/interfaces'
import ResourceFolderButton from './ResourceFolderButton'
import { config } from '../../config'
import { databaseName } from '../../utils/constants'
import { deleteFromBucket, getPathFromId, randString, uploadToBucket } from '../../utils/functions'
import { useEventListener } from '@iwbam/react-ez'

const FolderSidebar = ({ user, resource, setResource, openFile, jarContent }: { user: User, resource: Resource, setResource: React.Dispatch<React.SetStateAction<undefined | null | Resource>>, openFile: (file: ResourceFile) => void, jarContent?: ArrayBuffer | null }) => {

  const [isOpeningSidebar, setIsOpeningSidebar] = useState(false);

  useEventListener('click', (e) => {
    const target = e.target as HTMLElement;
    if (!target.closest('.sidebar') && !target.closest('.sidebar-toggle')) setIsOpeningSidebar(false);
  })

  // handle move a file or a folder
  const handleMove = async (id: string, newDirId: string) => {
    const newRoot: ResourceFolder = { ...resource.root };
    let targetItem: null | ResourceFile | ResourceFolder = null;
    // function to remove
    const searchAndRemove = (item: ResourceFolder | ResourceFile) => {
      // If the item is not a folder, return
      if (!(item as ResourceFolder).items) return;

      for (const childItem of (item as ResourceFolder).items) {
        if (childItem.id === id) { // target item
          targetItem = JSON.parse(JSON.stringify(childItem)); // set target item
          (item as ResourceFolder).items = (item as ResourceFolder).items.filter(i => i.id !== id); // remove from original folder
          return;
        }
      }
      (item as ResourceFolder).items.forEach(searchAndRemove);
    }
    // function to add to new folder
    const searchAndInsert = (item: ResourceFolder | ResourceFile) => {
      if ((item as ResourceFolder).items) { // is folder
        if (item.id === newDirId && targetItem !== null) { // put target item in target folder
          (item as ResourceFolder).items.push(targetItem);
          return;
        }
        (item as ResourceFolder).items.forEach(searchAndInsert);
      }
    }
    searchAndRemove(newRoot);
    searchAndInsert(newRoot);
    // update in database
    await fetch(config.api.database, { method: 'PATCH', body: JSON.stringify({ table: `${databaseName}_resources`, item_key: 'id', item_key_value: resource.id, keys: ['root', 'lastUpdated'], values: [newRoot, new Date().toISOString()] }) });
    setResource({ ...resource, root: JSON.parse(JSON.stringify(newRoot)) });
  }

  // handle delete a file or a folder
  const handleDelete = async (id: string) => {
    try {
      const newRoot: ResourceFolder = { ...resource.root };
      const filesShouldDelete: string[] = [];
      // Function to recursively search and update items
      const searchAndUpdate = (item: ResourceFolder | ResourceFile) => {
        // If the item is not a folder, return
        if (!(item as ResourceFolder).items) return;

        for (const childItem of (item as ResourceFolder).items) {
          if (childItem.id === id) { // item being deleted
            if ((childItem as ResourceFolder).items) { // add all files if it's folder
              // add all files in folder
              const addFilesIn = (folder: ResourceFolder) => {
                for (const chi of folder.items) {
                  if ((chi as ResourceFolder).items) {
                    addFilesIn(chi as ResourceFolder);
                  } else {
                    filesShouldDelete.push(chi.id);
                  }
                }
              }
              addFilesIn(childItem as ResourceFolder);
            } else { // add only itself if it's file
              filesShouldDelete.push(childItem.id);
            }
            (item as ResourceFolder).items = (item as ResourceFolder).items.filter(i => i.id !== id);
            return;
          }
        }
        (item as ResourceFolder).items.forEach(searchAndUpdate);
      }
      searchAndUpdate(newRoot);
      // update in database and s3
      await fetch(config.api.database, { method: 'PATCH', body: JSON.stringify({ table: `${databaseName}_resources`, item_key: 'id', item_key_value: resource.id, keys: ['root', 'lastUpdated'], values: [newRoot, new Date().toISOString()] }) });
      for (const fileId of filesShouldDelete) {
        deleteFromBucket(`${user.username}/${resource.id}/${fileId}`);
      }
      setResource({ ...resource, root: JSON.parse(JSON.stringify(newRoot)) });
    } catch (error) {
      console.log(error)
    }
  }

  // handle rename a file or a folder
  const handleRename = async (id: string, newName: string) => {
    const newRoot: ResourceFolder = { ...resource.root };
    const searchAndUpdate = (item: ResourceFolder | ResourceFile) => {
      if (item.id === id) {
        item.name = newName;
        return;
      } else if ((item as ResourceFolder).items) {
        (item as ResourceFolder).items.forEach(searchAndUpdate);
      }
    }
    searchAndUpdate(newRoot);
    // update in database
    await fetch(config.api.database, { method: 'PATCH', body: JSON.stringify({ table: `${databaseName}_resources`, item_key: 'id', item_key_value: resource.id, keys: id === resource.id ? ['name', 'root', 'lastUpdated'] : ['root', 'lastUpdated'], values: id === resource.id ? [newName, newRoot, new Date().toISOString()] : [newRoot, new Date().toISOString()] }) });
    setResource({ ...resource, root: JSON.parse(JSON.stringify(newRoot)) });
  }

  // add file
  const addFile = async (dirId: string, newFileName: string) => {
    const newRoot: ResourceFolder = { ...resource.root };
    const newId = randString(7) + '.' + newFileName.split('.').pop();
    const searchAndUpdate = (item: ResourceFolder | ResourceFile) => {
      if (item.id === dirId) {
        (item as ResourceFolder).items.push({
          id: newId,
          name: newFileName,
        });
        return;
      } else if ((item as ResourceFolder).items) {
        (item as ResourceFolder).items.forEach(searchAndUpdate);
      }
    }
    searchAndUpdate(newRoot);
    // update in database and s3
    await uploadToBucket(`${user.username}/${resource.id}/${newId}`, newFileName.endsWith('.java') ? `// A spigot plugin file called ${newFileName}\npackage ${user.username}.${resource.name.replaceAll(' ', '').toLowerCase()}${dirId === newRoot.id ? '' : '.'}${getPathFromId(newRoot, dirId).slice(1).replaceAll('/', '.')};` : '');
    await fetch(config.api.database, { method: 'PATCH', body: JSON.stringify({ table: `${databaseName}_resources`, item_key: 'id', item_key_value: resource.id, keys: ['root', 'lastUpdated'], values: [newRoot, new Date().toISOString()] }) });
    setResource({ ...resource, root: JSON.parse(JSON.stringify(newRoot)) });
  }

  // add folder
  const addFolder = async (dirId: string, newFolderName: string) => {
    const newRoot: ResourceFolder = { ...resource.root };
    const newId = randString(7);
    const searchAndUpdate = (item: ResourceFolder | ResourceFile) => {
      if (item.id === dirId) {
        (item as ResourceFolder).items.push({
          id: newId,
          name: newFolderName,
          items: []
        });
        return;
      } else if ((item as ResourceFolder).items) {
        (item as ResourceFolder).items.forEach(searchAndUpdate);
      }
    }
    searchAndUpdate(newRoot);
    // update in database and s3
    await fetch(config.api.database, { method: 'PATCH', body: JSON.stringify({ table: `${databaseName}_resources`, item_key: 'id', item_key_value: resource.id, keys: ['root', 'lastUpdated'], values: [newRoot, new Date().toISOString()] }) });
    setResource({ ...resource, root: JSON.parse(JSON.stringify(newRoot)) });
  }

  return (
    <>
      <button className='md:hidden sidebar-toggle p-1 w-8' onClick={() => setIsOpeningSidebar(true)}><i className='fa-solid fa-bars' /></button>
      <aside className={`sidebar trans-2 h-full md:relative scroller w-2/3 fixed md:w-56 z-20 bg-neutral-900 border-r border-gray-800 ${isOpeningSidebar ? 'left-0' : '-left-full md:left-0'}`}>
        <ResourceFolderButton
          resource={resource}
          folder={resource.root}
          showButtonAlways
          initialOpen
          isRoot
          jarContent={jarContent}
          style={{ borderBottom: '1px solid #fff2' }}
          fontStyle={{ fontWeight: 'bold' }}
          handleMove={handleMove}
          handleDelete={handleDelete}
          handleRename={handleRename}
          addFolder={addFolder}
          addFile={addFile}
          openFile={openFile}
        />
      </aside>
    </>
  )
}

export default FolderSidebar